import React, { useCallback, useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useDataProvider } from "react-admin";
import { makeStyles } from "@material-ui/core";
import { useField } from "react-final-form";

interface IParentAndPicSearchBox {
  isDisabled: boolean;
  schoolId?: number;
}

const useStyles = makeStyles({
  override: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      height: "50px",
      "& fieldset": {
        borderColor: "#EAEAEA !important",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA !important",
      },
    },
  },
});

const ParentAndPICSearchBox = ({
  isDisabled,
  schoolId,
}: IParentAndPicSearchBox) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<IParent[]>([]);
  const [selectedValue, setSelectedValue] = useState<IParent>(null);

  const classes = useStyles();
  const { customGetListWithStaticParams, getOne } = useDataProvider();

  const parentIdField = useField("parentId");

  const getParentsAndPICS = useCallback(
    async (searchString: string) => {
      try {
        const personInChargesResponse = await customGetListWithStaticParams(
          `personInCharges?pageSize=50&search=${searchString}`
        );
        const parentsResponse = await customGetListWithStaticParams(
          `parents?pageSize=50&search=${searchString}&filter=${JSON.stringify({
            SchoolId: schoolId,
          })}`
        );

        setOptions([...personInChargesResponse.data, ...parentsResponse.data]);
      } catch (error) {
        console.log("error", error);
      }
    },
    [schoolId]
  );

  // Helper function to get the selected Parent or PIC by selected id
  const getSelectedValue = async (selectedId: string) => {
    try {
      const [parentRes, picRes] = await Promise.allSettled([
        getOne("parents", { id: selectedId }),
        getOne("personInCharges", { id: selectedId }),
      ]);

      // Pick whichever one succeeded and has data
      return (parentRes.status === "fulfilled" && parentRes.value?.data) ||
        (picRes.status === "fulfilled" && picRes.value?.data) ||
        null; // If both fail, return null
    } catch (err) {
      console.log("Failed to fetch selected value", err);
    }
  };

  // Get pre selected parent or pic
  // Only run if there is an existing value in the field
  useEffect(() => {
    if (parentIdField.input.value) {
      getSelectedValue(parentIdField.input.value).then((result) => {
        if (result) {
          setSelectedValue(result as IParent);
          setOptions([result] as IParent[]);
        }
      })
    }
  }, [parentIdField.input.value]);


  // Initial load, loads first 50 results from both parents & PICs
  useEffect(() => {
    if (!isDisabled) {
      getParentsAndPICS("");
    }
  }, [isDisabled, getParentsAndPICS]);

  // search when typing
  useEffect(() => {
    if (!isDisabled && inputValue) {
      getParentsAndPICS(inputValue);
    }
  }, [inputValue, isDisabled, getParentsAndPICS]);


  return (
    <Autocomplete
      className={classes.override}
      id="combo-box"
      options={options}
      value={selectedValue}
      getOptionLabel={(option) =>
        `${option.id} - ${option.firstName} ${option.lastName} - ${
          option.company ? option.company.name : "Parent"
        }`
      }
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(event, newInputValue) => {
        if (newInputValue) {
          parentIdField.input.onChange((newInputValue as IParent).id);
        } else {
          parentIdField.input.onChange("");
        }
      }}
      disabled={isDisabled}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Parent or PIC Name *"
          variant="outlined"
        />
      )}
    />
  );
};

export default ParentAndPICSearchBox;
